import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, MetricSelectOrigin as Origin, PopupData, Service } from 'webapp-types';
import { equal, isAlive } from 'webapp/utils/common';
import { sanitize } from 'webapp/utils/html';

import themed from 'webapp/decorators/themed';
import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormMetricSelectPopupComponent from 'webapp/components/form/metric-select/popup';
import type { MetricItem } from 'webapp/components/form/metric-select/popup/index';
import type { OptionRaw } from 'webapp/components/form/select';
import MetricLibraryPopupsMetricBasicInfoTooltipComponent from 'webapp/components/metric-library/popups/metric-basic-info-tooltip';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type { MetricFromClient } from 'webapp/models-custom';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type { SourceCategory } from 'webapp/models-custom/filter-sort/metric-select';
import Metric from 'webapp/models-custom/metric';
import type StatusObject from 'webapp/models-custom/status-object';

import type { onConnectSourceResult } from 'webapp/services/connect';

export type { Origin };

interface Args {
	selected: DSSetup.MetricData | null | undefined;
	origin?: Origin;
	onChange: (selected: DSSetup.MetricSetupData) => void;
	isDisabled?: boolean;
	disableNotConnected?: boolean;
	skipConnected?: boolean;
	skipGoals?: boolean;
	skipHorizon?: boolean;
	skipCreateCustomMetrics?: boolean;
	skipCustomGenerics?: boolean;
	disableYouTubeMetrics?: boolean; // limitation for creating scripts from YouTube
	hideFavoriteBtn?: boolean;
	hideConnectBtn?: boolean;
	hideNoResultsActions?: boolean;
	filterSortSourceOptions?: FilterOption<SourceCategory | string>[];
	placeholder?: string;
	originForMetricFavorite?: string;
	theme?: string;
	size?: string; // supported sizes: normal, big
	onAfterMetricEdit?: (metric: Metric) => void;
	customClientMetrics?: MetricFromClient[];
	hideOptionToSelectDimensions?: boolean;
}

@themed()
export default class FormMetricSelectComponent extends Component<Args> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('popup') declare popupService: Service.Popup;

	@tracked options: OptionRaw<string>[] = [];

	declare theme: string; // from @themed
	declare themeClass: string; // from @themed
	declare sizeClass: string; // from @themed

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.formatOption();
	}

	get selectedMetric(): MetricItem | null {
		const selected = this.args.selected;

		if (!selected) {
			return null;
		}

		const clientMetrics = this.args.customClientMetrics || [];

		if (clientMetrics.length) {
			return (
				clientMetrics.find(
					(item) =>
						item.metricData?.key === selected.key &&
						item.metricData?.spaceAccessId === selected.spaceAccessId,
				) ?? null
			);
		} else {
			return this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
				selected.key,
				selected.spaceAccessId,
			);
		}
	}

	get isPlaceholder(): boolean {
		return !this.selectedMetric;
	}

	get selectedMetricName(): string {
		const selectedMetric = this.selectedMetric;
		if (selectedMetric) {
			let name = selectedMetric.name;
			if (selectedMetric.isDeprecated) {
				name += ' (deprecated)';
			}
			return name;
		} else {
			return this.args.placeholder || 'Select metric';
		}
	}

	get sourceName(): string {
		return this.selectedMetric?.sourceName || '';
	}

	get sourceAvatar(): string {
		return this.selectedMetric?.sourceAvatar || '';
	}

	get statusObject(): StatusObject | null {
		return this.selectedMetric?.source?.statusObject || null;
	}

	get isSourceErrorWarningShown(): boolean {
		return this.statusObject?.level === 'error';
	}

	get isConnectButtonShown(): boolean {
		return (
			!!this.args.selected &&
			!this.selectedMetric?.source &&
			!this.args.hideConnectBtn &&
			!!this.selectedSourceType
		);
	}

	get isEditButtonShown(): boolean {
		return !!this.selectedMetric?.canEdit;
	}

	get hasIconPaddingClass(): boolean {
		return this.isSourceErrorWarningShown || !!this.selectedMetric; // if this.selectedMetric is set, we show MetricBasicInfoTooltip
	}

	get extraClass(): string {
		const cssClasses: string[] = [],
			selectedMetric = this.selectedMetric;

		if (!this.args.selected) {
			cssClasses.push('empty');
		}

		if (this.hasIconPaddingClass) {
			cssClasses.push('show-padding-icon');
		}

		if (this.isConnectButtonShown) {
			cssClasses.push('show-connect-icon');
		}

		if (this.isEditButtonShown) {
			cssClasses.push('show-padding-edit');
		}

		// Icon badges for metric types are now shown via ui-icon in template

		if (selectedMetric?.isDeprecated) {
			cssClasses.push('deprecated');
		}

		return cssClasses.join(' ');
	}

	get selectedSourceType() {
		return this.selectedMetric?.sourceType;
	}

	formatSourceLabel(metric: MetricItem): string {
		const sourceName = metric.sourceName;
		return htmlSafe(
			`<span title="${sanitize(sourceName)}"><img class="avatar" src="${metric.sourceAvatar}">${sourceName}</span>`,
		).toHTML();
	}

	@action
	getPopupProps(): PopupData.Form.MetricSelect {
		return {
			metric: this.selectedMetric,
			origin: this.args.origin,
			onChange: this.args.onChange,
			disableNotConnected: this.args.disableNotConnected,
			skipConnected: this.args.skipConnected,
			skipGoals: this.args.skipGoals,
			skipHorizon: this.args.skipHorizon,
			skipCreateCustomMetrics: this.args.skipCreateCustomMetrics,
			skipCustomGenerics: this.args.skipCustomGenerics,
			disableYouTubeMetrics: this.args.disableYouTubeMetrics,
			hideFavoriteBtn: this.args.hideFavoriteBtn,
			hideConnectBtn: this.args.hideConnectBtn,
			hideNoResultsActions: this.args.hideNoResultsActions,
			filterSortSourceOptions: this.args.filterSortSourceOptions,
			originForMetricFavorite: this.args.originForMetricFavorite,
			customClientMetrics: this.args.customClientMetrics,
			hideOptionToSelectDimensions: this.args.hideOptionToSelectDimensions,
		};
	}

	get popupClass() {
		return `popup-metric-select ${this.extraClass || ''}`;
	}

	get selected() {
		return this.selectedMetric?.id || '';
	}

	get placeholder() {
		return this.args.placeholder || 'Select metric';
	}

	@action
	openPopup() {
		if (this.args.isDisabled) {
			return;
		}

		const props = this.getPopupProps();
		const basePopupProps = {
			popupClass: this.popupClass,
			theme: this.args.theme,
			allowStacked: true,
			allowMultipleInstances: true,
			hasOverlay: true,
			closeOnOverlayClick: true,
		};

		return this.popupService
			.createModal(FormMetricSelectPopupComponent, basePopupProps)
			.open(props);
	}

	@action
	formatOption(): void {
		const selectedMetric = this.selectedMetric;

		if (!selectedMetric) {
			return;
		}

		this.options = [
			{
				value: selectedMetric.id!,
				label: selectedMetric.name,
				label2: this.formatSourceLabel(selectedMetric),
				label4: `${selectedMetric.name} (${selectedMetric.sourceName})`,
			},
		];
	}

	@action
	editMetric(event: MouseEvent): void {
		event.stopPropagation();

		const selectedMetric = this.selectedMetric;
		if (selectedMetric instanceof Metric) {
			selectedMetric.edit(this.args.origin, (metrics: Metric[]) => {
				// if results are multiple (f.ex script), find one from the same source as this
				// if not, send this.change
				let newMetric = metrics[0]!;
				if (metrics.length > 1 && !equal(selectedMetric.metricData, newMetric.metricData)) {
					const metricFromCurrentSource = metrics.find(
						(m) => m.spaceAccessId === selectedMetric.spaceAccessId,
					);
					if (metricFromCurrentSource) {
						newMetric = metricFromCurrentSource;
					}
				}
				if (newMetric && !equal(newMetric.metricData, selectedMetric.metricData)) {
					this.change(newMetric.metricData!);
				}

				if (this.args.onAfterMetricEdit) {
					this.args.onAfterMetricEdit(newMetric);
				}
			});
		}
	}

	@action
	connectSource(): void {
		const selectedMetric = this.selectedMetric;
		if (!selectedMetric) {
			return;
		}

		void this.selectedSourceType
			?.connectNew({ origin: this.args.origin })
			.then((data: onConnectSourceResult) => {
				if (data.isSuccess && isAlive(this)) {
					const spaceAccessId = data.source.id;
					void this.mmMetricsService
						.resolveGenericMetricWithSpecific(selectedMetric.key, spaceAccessId)
						.then((metricData) => {
							this.change(metricData);
						});
				}
			});
	}

	@action
	change(selected: DSSetup.MetricSetupData): void {
		this.args.onChange(selected);
	}
	<template>
		<div
			role="button"
			class={{joinClasses
				(array
					"metric-select"
					this.themeClass
					this.sizeClass
					this.extraClass
					(if @isDisabled "disabled")
				)
			}}
			{{on "click" this.openPopup}}
			{{didUpdate
				this.formatOption
				@selected
				this.selectedMetric.source.title
				this.selectedMetric.name
			}}
		>
			<div class="metric-select__content">
				<div
					class={{joinClasses
						(array
							"metric-select__name"
							(if this.selectedMetric "metric-select__name--selected")
						)
					}}
				>
					<span class="metric-select__name-text">{{this.selectedMetricName}}</span>
					{{#if this.selectedMetric.isTypeMetricCalculation}}
						<CommonUiIconComponent
							@name="Calculator"
							@size="sm"
							@class="metric-type-badge"
						/>
					{{else if this.selectedMetric.isTypeCustomQuery}}
						<CommonUiIconComponent
							@name="Data"
							@size="sm"
							@class="metric-type-badge metric-type-badge--query"
						/>
					{{/if}}
				</div>
				{{#if this.selectedMetric}}
					<div class="metric-select__source">
						<img
							alt="Source avatar"
							class="metric-select__source-avatar"
							src={{this.sourceAvatar}}
						/>
						<div class="metric-select__source-title" title={{this.sourceName}}>
							{{this.sourceName}}
						</div>
					</div>
				{{/if}}
			</div>

			<div class="metric-select__actions">
				{{#if this.isConnectButtonShown}}
					{{#if this.selectedSourceType}}
						<SourceConnectButtonComponent
							@sourceType={{this.selectedSourceType}}
							@onConnect={{this.connectSource}}
							@text="Connect"
							@size="tiny"
							@theme="green"
							@stopPropagation={{true}}
						/>
					{{/if}}
				{{else}}
					{{#if this.isEditButtonShown}}
						{{! template-lint-disable no-nested-interactive }}
						<a
							role="button"
							class="metric-select__edit-button metric-select__edit-button--{{@theme}}"
							{{on "click" this.editMetric}}
						>EDIT
						</a>
					{{/if}}

					{{#if this.isSourceErrorWarningShown}}
						<ToolTipWithIconComponent
							@text={{this.statusObject.statusTitleAndDescriptionWithAction}}
							@theme={{@theme}}
							@iconName="AlertFill"
							@maxWidth={{235}}
						/>
					{{else if this.selectedMetric}}
						<MetricLibraryPopupsMetricBasicInfoTooltipComponent
							@model={{this.selectedMetric}}
							@mouseLeaveDelay={{500}}
						>
							<CommonUiIconComponent @name="InfoFill" @size="sm" />
						</MetricLibraryPopupsMetricBasicInfoTooltipComponent>
					{{/if}}
				{{/if}}
				<CommonUiIconComponent @name="ChevronDown" @size="sm" />
			</div>

		</div>
	</template>
}
